import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES } from "../data/content";

export const Hero = () => {
  const { t } = useLang();
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" className="media-dark relative min-h-screen flex items-center overflow-hidden pt-24" data-testid="hero-section">
      {/* Background media */}
      <div className="absolute inset-0">
        <img src={IMAGES.hero} alt="" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#030305]/70 via-[#030305]/50 to-[#030305]" />
        <div className="absolute inset-0 grid-bg" />
        {/* scanline */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="scanline absolute left-0 right-0 h-24 bg-gradient-to-b from-transparent via-[#00F0FF]/5 to-transparent" />
        </div>
      </div>
      <div className="orb w-96 h-96 bg-[#00F0FF]/30 -left-20 top-40" />
      <div className="orb w-80 h-80 bg-[#FF0055]/20 right-0 bottom-20" style={{ animationDelay: "3s" }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-[#00F0FF]/30 mb-8"
          data-testid="hero-eyebrow"
        >
          <Sparkles size={14} className="text-cyan" />
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-300">{t("hero_eyebrow")}</span>
        </motion.div>

        <h1 className="font-head font-extrabold text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-4xl" data-testid="hero-title">
          <motion.span
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="block text-white glow-text"
          >
            {t("hero_title_1")}
          </motion.span>
          <motion.span
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="block text-cyan glow-text"
          >
            {t("hero_title_2")}
          </motion.span>
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.45 }}
          className="mt-8 max-w-2xl text-base sm:text-lg text-zinc-400 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {t("hero_subtitle")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => go("contact")}
            className="btn-neon group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-semibold uppercase tracking-wide font-mono text-sm"
          >
            {t("hero_cta_primary")}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => window.dispatchEvent(new CustomEvent("open-chat"))}
            className="btn-ghost inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-semibold uppercase tracking-wide font-mono text-sm text-white"
          >
            {t("hero_cta_secondary")}
          </button>
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-xs font-mono uppercase tracking-[0.3em] text-zinc-600 animate-pulse">
        {t("hero_scroll")}
      </div>
    </section>
  );
};
